import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { IconButton, Popover, Tooltip } from '@mui/material'
import BookmarksIcon from '@mui/icons-material/Bookmarks'
import { useBookmarks } from '@/hooks/useBookmarks'
import { useContentBundle } from '@/hooks/useContentBundle'
import type { ContentType } from '@/types/api'

export function BookmarksMenu() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { bookmarks } = useBookmarks()
  const { data: bundle } = useContentBundle()
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLButtonElement>(null)

  const hasBookmarks = bookmarks.length > 0

  const resolvePracticeAreaName = (contentType: ContentType, entityId: number): string => {
    if (!bundle) return ''
    if (contentType === 'PracticeArea') {
      return bundle.practiceAreas.find((pa) => pa.id === entityId)?.displayName ?? ''
    }
    if (contentType === 'Practice') {
      const practice = bundle.practices.find((p) => p.id === entityId)
      return bundle.practiceAreas.find((pa) => pa.id === practice?.practiceAreaId)?.displayName ?? ''
    }
    return ''
  }

  const sortedBookmarks = useMemo(
    () =>
      [...bookmarks].sort((a, b) =>
        resolvePracticeAreaName(a.contentType, a.entityId).localeCompare(
          resolvePracticeAreaName(b.contentType, b.entityId)
        )
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bookmarks, bundle]
  )

  /** Resolve a human-readable label for a bookmark from the content bundle. */
  const resolveLabel = (contentType: ContentType, entityId: number): string => {
    if (!bundle) return `#${entityId}`
    if (contentType === 'PracticeArea') {
      return bundle.practiceAreas.find((pa) => pa.id === entityId)?.displayName ?? `PA #${entityId}`
    }
    if (contentType === 'Practice') {
      return bundle.practices.find((p) => p.id === entityId)?.displayName ?? `Practice #${entityId}`
    }
    return `${contentType} #${entityId}`
  }

  /** Build the navigation URL for a bookmark. */
  const resolveUrl = (contentType: ContentType, entityId: number): string => {
    if (contentType === 'PracticeArea') return `/practice-area/${entityId}`
    if (contentType === 'Practice') return `/practice/${entityId}`
    return '/'
  }

  const handleItemClick = (contentType: ContentType, entityId: number) => {
    setOpen(false)
    navigate(resolveUrl(contentType, entityId))
  }

  return (
    <>
      <Tooltip title={t('bookmarksMenu.title')} placement="bottom">
        <IconButton
          ref={anchorRef}
          onClick={() => setOpen((o) => !o)}
          aria-label={t('bookmarksMenu.openMenu')}
          aria-haspopup="true"
          aria-expanded={open}
          data-tour="bookmarks-btn"
          className={`bookmarks-menu-btn${hasBookmarks ? ' bookmarks-menu-btn--has-items' : ''}`}
          size="small"
          sx={{
            color: hasBookmarks ? 'var(--color-white)' : 'var(--color-on-dark-55)',
            '&:hover': { color: 'var(--color-white)', backgroundColor: 'var(--color-on-dark-10)' },
          }}
        >
          <BookmarksIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            className: 'bookmarks-menu-paper',
            sx: { mt: '4px', borderRadius: '6px', boxShadow: 'var(--shadow-menu-soft)' },
          },
        }}
      >
        <div className="bookmarks-menu">
          <div className="bookmarks-menu-header">{t('bookmarksMenu.title')}</div>

          {bookmarks.length === 0 ? (
            <div className="bookmarks-menu-empty">{t('bookmarksMenu.noBookmarksYet')}</div>
          ) : (
            <div className="bookmarks-menu-list">
              {sortedBookmarks.map((bm) => {
                const label = resolveLabel(bm.contentType, bm.entityId)
                const type = bm.contentType === 'PracticeArea' ? t('bookmarksMenu.typeLabel_PracticeArea') : t('bookmarksMenu.typeLabel_Practice')
                return (
                  <button
                    key={bm.id}
                    className="bookmarks-menu-item"
                    onClick={() => handleItemClick(bm.contentType, bm.entityId)}
                    title={label}
                  >
                    <span className="bookmarks-menu-item-type">{type}</span>
                    <span className="bookmarks-menu-item-label">{label}</span>
                  </button>
                )
              })}
            </div>
          )}
        </div>
      </Popover>
    </>
  )
}
